import { useCallback, useEffect, useMemo, useState } from 'react';

import { isBefore, parseISO, startOfDay } from 'date-fns';

import { RateSource } from '@/modules/common/types/rate-source';
import { useTeamMemberRatesOnProject } from '@/modules/team/hooks/use-team-member-rates-on-project';
import { getTodayISOString, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import {
  buildInitialRateOptionsFromPeriods,
  getEffectiveDateForRatesQuery,
  hasRateParamsChanged,
} from '../../components/modals/assignment-modal/helpers/assignment-modal-rates';
import { getRateOptionsFromApi } from '../../helpers';
import { RateOption } from '../../types';

interface UseAssignmentRateQueriesParams {
  open: boolean;
  userId: string;
  projectId: string;
  currentRate: string;
  committedDateFrom: Date | null | undefined;
  committedDateTo: Date | null | undefined;
  isEditMode: boolean;
  initialData: {
    projectId?: string;
    rate?: string;
    currency?: string;
    rateSource?: RateSource;
    projectBoundRateSource?: RateSource | null;
    dateRange?: { from?: Date; to?: Date };
  };
}

interface UseAssignmentRateQueriesResult {
  ratesData: ReturnType<typeof useTeamMemberRatesOnProject>['data'];
  isLoadingRates: boolean;
  rateOptions: RateOption[];
  isBeforeContractStart: boolean;
  /** True when query 2 is skipped and query 1 data is reused */
  shouldReuseContractStartData: boolean;
  /**
   * True when the rates API has returned data, so `rateOptions` contains real
   * project/user sources (not just the minimal Custom-only fallback). Used to
   * decide whether the rate-source dropdown should be selectable.
   */
  hasRealRateOptions: boolean;
  /**
   * True once a member, project and start date are known, so the rate-options query *could* run.
   * The rate-source select uses this to stay openable even before any options have loaded —
   * opening it is what triggers the fetch.
   */
  canLoadRateOptions: boolean;
  /**
   * Hand to the rate-source dropdown's `onOpenChange`. In edit mode query 2 is deferred until the
   * user changes the project or dates, which leaves `ratesData` undefined for an assignment whose
   * smart effective date differs from its start date — so the dropdown would offer only the
   * assignment's own source and Custom (and nothing at all when that source IS Custom). Opening
   * the dropdown is the moment the user needs the real list, so it lifts the deferral.
   */
  onRateSourceDropdownOpen: (isOpen: boolean) => void;
}

/**
 * Orchestrates the two rate queries needed by the assignment modal:
 *
 * 1. **Contract-start query** — always fires with effectiveDate = assignment start date.
 *    Populates `nearestEffectiveStartDate` for the "before contract start" warning.
 *
 * 2. **Rate options query** — fires with a "smart" effective date (past → end date,
 *    future → start date, current → today). Skipped when its result would duplicate
 *    query 1 or when the assignment is before the contract start.
 */
export function useAssignmentRateQueries({
  open,
  userId,
  projectId,
  currentRate,
  committedDateFrom,
  committedDateTo,
  isEditMode,
  initialData,
}: UseAssignmentRateQueriesParams): UseAssignmentRateQueriesResult {
  const assignmentOverride = currentRate ? parseFloat(currentRate) : undefined;

  // Set when the user opens the rate-source dropdown, to lift the edit-mode deferral of query 2.
  const [hasRequestedRateOptions, setHasRequestedRateOptions] = useState(false);

  useEffect(() => {
    if (!open) setHasRequestedRateOptions(false);
  }, [open]);

  const onRateSourceDropdownOpen = useCallback((isOpen: boolean) => {
    if (isOpen) setHasRequestedRateOptions(true);
  }, []);

  const baseRateQueryParams = {
    teamMemberId: userId ? Number(userId) : 0,
    projectId: projectId ? Number(projectId) : 0,
    enabled: open && Boolean(userId) && Boolean(projectId) && Boolean(committedDateFrom),
  };

  // --- Query 1: contract-start check ---
  const contractStartEffectiveDate = committedDateFrom
    ? normalizeToStartOfDayUTC(committedDateFrom).toISOString()
    : getTodayISOString();

  const { data: contractStartRatesData } = useTeamMemberRatesOnProject({
    ...baseRateQueryParams,
    effectiveDate: contractStartEffectiveDate,
  });

  const isBeforeContractStart = Boolean(
    contractStartRatesData?.nearestEffectiveStartDate &&
      committedDateFrom &&
      isBefore(
        startOfDay(committedDateFrom),
        startOfDay(parseISO(contractStartRatesData.nearestEffectiveStartDate))
      )
  );

  // --- Query 2: rate options ---
  const hasInitialRateFromPeriods = isEditMode && !!initialData?.rate;
  const hasUserChangedRateParams = useMemo(
    () =>
      hasRateParamsChanged({
        isEditMode,
        hasInitialRateFromPeriods,
        projectId,
        initialProjectId: initialData?.projectId,
        committedDateFrom,
        committedDateTo,
        initialDateFrom: initialData?.dateRange?.from,
        initialDateTo: initialData?.dateRange?.to,
      }),
    [
      isEditMode,
      hasInitialRateFromPeriods,
      projectId,
      initialData?.projectId,
      committedDateFrom,
      committedDateTo,
      initialData?.dateRange?.from,
      initialData?.dateRange?.to,
    ]
  );

  const smartEffectiveDateForRates = useMemo(
    () => getEffectiveDateForRatesQuery(committedDateFrom, committedDateTo),
    [committedDateFrom, committedDateTo]
  );

  // Skip query 2 when its result would duplicate query 1
  const smartDateMatchesContractStartDate =
    smartEffectiveDateForRates === contractStartEffectiveDate;
  const shouldReuseContractStartData = smartDateMatchesContractStartDate || isBeforeContractStart;

  const { data: ratesQueryData, isLoading: isLoadingRates } = useTeamMemberRatesOnProject({
    ...baseRateQueryParams,
    effectiveDate: smartEffectiveDateForRates,
    enabled:
      baseRateQueryParams.enabled &&
      (hasUserChangedRateParams || hasRequestedRateOptions) &&
      !!contractStartRatesData &&
      !shouldReuseContractStartData,
  });

  const ratesData = shouldReuseContractStartData ? contractStartRatesData : ratesQueryData;

  // --- Build rate options ---
  const rateOptions = useMemo(() => {
    if (ratesData) {
      return getRateOptionsFromApi({ ratesData, assignmentOverride }).filter(
        o => !isBeforeContractStart || o.value !== RateSource.ProjectBased
      );
    }

    // In edit mode with initial rate data, build minimal options so the form
    // can display the current source without waiting for the API
    if (isEditMode && initialData?.rateSource && initialData?.rate) {
      return buildInitialRateOptionsFromPeriods({
        rateSource: initialData.rateSource,
        rate: initialData.rate,
        currency: initialData.currency,
        projectBoundRateSource: initialData.projectBoundRateSource,
        assignmentOverride,
      });
    }

    return getRateOptionsFromApi({ ratesData: undefined, assignmentOverride });
  }, [
    ratesData,
    assignmentOverride,
    isBeforeContractStart,
    isEditMode,
    initialData?.rateSource,
    initialData?.rate,
    initialData?.currency,
    initialData?.projectBoundRateSource,
  ]);

  return {
    ratesData,
    isLoadingRates,
    rateOptions,
    isBeforeContractStart,
    /** True when query 2 is skipped and query 1 data is reused */
    shouldReuseContractStartData,
    hasRealRateOptions: Boolean(ratesData),
    canLoadRateOptions: baseRateQueryParams.enabled,
    onRateSourceDropdownOpen,
  };
}
